import i18n from "i18next";
import { useFirstExportToast } from "@/components/onboarding/useFirstExportToast";
import { useState, useCallback, useEffect } from "react";
import { useDocActions } from "@/components/documents/useDocActions";
import { useNavigate, useLocation, useSearchParams } from "react-router";
import {
  IconArrowLeft, IconDownload, IconSun, IconMoon, IconShare2,
  IconPackage, IconSearch, IconChevronDown, IconCopy,
  IconDots, IconSettings, IconTemplate, IconTrash,
} from "@tabler/icons-react";
import { SaveAsTemplateDialog } from "@/components/templates/SaveAsTemplateDialog";
import { LanguageToggle } from "@/components/shared/LanguageToggle";
import { useApiContext, API_BASE } from "@/contexts/ApiContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel,
} from "@/components/lilia-ui/dropdown-menu";
import { ExportDialog } from "../dialogs/ExportDialog";
import { ShareDrawer } from "@/components/sharing/ShareDrawer";
import { DocumentStamp } from "./DocumentStamp/DocumentStamp";
import type { AutoSaveStatus } from "@/hooks/useAutoSave";
import type { Document } from "@/lib/api";
import { cn } from "@/lib/cn";
import useDarkMode from "@/utils/hooks/useDarkMode";
import { ThemePicker } from "@/components/theme/ThemePicker";
import type { UserPresence } from "@/hooks/usePresence";
import { useTranslation } from "@/utils/hooks/useTranslation";
import { testIds } from "@/testIds";
import type { EditorArrangement } from "@/hooks/useChatFirstLayout";

export type EditorMode = "visual" | "split" | "source";

/* ds30 top-bar button system — exact transcription of lilia-design-system.css
 * `.lilia-btn` + `.lilia-icon-btn` so the right cluster matches the authoritative
 * spec to the pixel:
 *   TOPBAR_CHIP    — bordered surface pill (Article ▾ · Share · EN · source toggle):
 *                    h=--lilia-control-h (30px), padding 0 11px, radius 8, border,
 *                    surface bg, 12px/500 text, gap 6.
 *   TOPBAR_ICONBTN — square ghost (search · theme · ⋮): 30×30, radius 6, no border,
 *                    transparent, text-secondary → text on hover.
 * Heights driven by the --lilia-control-h token (→ 26px under compact density). */
const TOPBAR_CHIP =
  "shrink-0 inline-flex items-center gap-1.5 h-[var(--lilia-control-h,30px)] px-[11px] rounded-[8px] text-[12px] font-medium " +
  "border border-[var(--lilia-border-strong)] bg-[var(--lilia-surface)] text-[var(--lilia-text)] " +
  "hover:bg-[var(--lilia-hover)] transition-colors";

interface TopBarProps {
  /**
   * Compact chrome: render only the document-scoped cluster, for hosting
   * inside the shell bar instead of as a second row. The row it replaces was
   * carrying a duplicate search and a duplicate theme toggle, plus a layout
   * preset the pane tabs already express — 48px of every screen for very
   * little. Design: lilia-docs/design-handoffs/2026-09-11-editor-chrome/ §4.
   */
  compact?: boolean;
  documentId: string;
  title: string;
  onTitleChange: (title: string) => void;
  mode: EditorMode;
  onModeChange: (mode: EditorMode) => void;
  /** Source-pane toggle — wired per mode (Flow: split; Block: LaTeX sidebar).
   *  When provided, the top-bar "View source" button uses these. */
  onToggleSource?: () => void;
  sourceShown?: boolean;
  saveStatus: AutoSaveStatus;
  isDirty: boolean;
  onSaveNow: () => void;
  lastSavedAt?: Date | null;
  isPublic?: boolean;
  shareLink?: string;
  shareSlug?: string;
  ownerId?: string;
  presenceUsers?: UserPresence[];
  onPasteLatex?: () => void;
  blockCount?: number;
  /** Number of validation issues — when > 0, the pip turns terracotta and
   *  reads `● N issues` instead of the save state (LILIA-114, Claude Design
   *  bonus 2026-04-29). Quiet → loud is the whole mechanism; without the
   *  number, the state change is just a hue and gets ignored. */
  validationIssueCount?: number;
  /** Open the global command palette (⌘K). Hint button in the chrome calls this. */
  onOpenCommandPalette?: () => void;
  /** Open the LaTeX packages modal (catalog browser + install/remove). */
  onOpenPackages?: () => void;
  /** Opens Document settings (paper, fonts, columns) from the ⋯. */
  onOpenSettings?: () => void;
  /** Full document — passed through so the DocumentStamp under the title
   *  can render the class label + open the document-settings popover
   *  (LILIA-122). Optional so the topbar still renders during loading. */
  document?: Document;
  /** True while the document is loading — DocumentStamp renders skeletons. */
  documentLoading?: boolean;
  /** True when the user has read-only access — DocumentStamp shows the
   *  no-permission state. */
  documentReadOnly?: boolean;
  /**
   * Chat-First arrangement (handoff 2026-07-15): "Chat first" = Ask Lilia main +
   * artifact document pane; Side = classic doc primary + docked Ask Lilia.
   * When omitted, the layout switch is hidden (e.g. Studio not yet wired).
   */
  arrangement?: EditorArrangement;
  onArrangementChange?: (layout: EditorArrangement) => void;
}

/**
 * Editor TopBar — Quiet redesign (Pass 3, 2026-04-28).
 * One 48px row. Tokens scoped via .editor-shell (see src/styles/editor.scss).
 *
 * Layout:
 *   [back · breadcrumb] [serif title] [validation pip]
 *     ──────────── flex spacer ────────────
 *   [⌘K hint] [mode segment] [source-pane toggle]
 *   [presence] [share] [overflow]
 */
export function TopBar({
  compact = false,
  documentId,
  title,
  onTitleChange,
  onToggleSource,
  sourceShown,
  isDirty,
  onSaveNow,
  isPublic,
  shareLink,
  shareSlug,
  ownerId,
  onOpenSettings,
  presenceUsers = [],
  validationIssueCount = 0,
  onOpenCommandPalette,
  document: documentObj,
  documentLoading = false,
  documentReadOnly = false,
}: TopBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('editor');
  const fromPath: string | undefined = (location.state as { from?: string } | null)?.from;
  const fromLabel: string | undefined = (location.state as { fromLabel?: string } | null)?.fromLabel;
  const [isDark, setIsDark] = useDarkMode();
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  // First win 1b: the first export's toast offers Share.
  useEffect(() => {
    const open = () => setShowShareDialog(true);
    window.addEventListener("lilia:open-share", open);
    return () => window.removeEventListener("lilia:open-share", open);
  }, []);

  // "Invite accepted" opens Share with the new person's row flashed (Olivia,
  // 10 Oct): /d/:id?share=open&flash=:userId. The address is tidied after.
  const [shareParams, setShareParams] = useSearchParams();
  useEffect(() => {
    if (shareParams.get("share") !== "open") return;
    const flash = shareParams.get("flash");
    setShowShareDialog(true);
    const next = new URLSearchParams(shareParams);
    next.delete("share");
    next.delete("flash");
    setShareParams(next, { replace: true });
    if (!flash) return;
    let tries = 0;
    const timer = window.setInterval(() => {
      const row = window.document.querySelector<HTMLElement>(`[data-testid="share-collaborator"][data-user-id="${CSS.escape(flash)}"]`);
      if (!row && ++tries < 30) return;
      window.clearInterval(timer);
      if (!row) return;
      row.setAttribute("data-flash", "true");
      row.scrollIntoView({ block: "nearest" });
      window.setTimeout(() => row.removeAttribute("data-flash"), 2400);
    }, 150);
    return () => window.clearInterval(timer);
  }, [shareParams, setShareParams]);
  const [localTitle, setLocalTitle] = useState(title);
  const { duplicateDoc, trashDoc } = useDocActions();
  const [makingCopy, setMakingCopy] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);

  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalTitle(e.target.value);
      onTitleChange(e.target.value);
    },
    [onTitleChange]
  );

  // Validation issues ride a separate pip beside the sync indicator —
  // the two answer different questions and never override each other
  // (coexistence Option 1; see the sync-indicator design handoff).
  const hasValidationIssues = validationIssueCount > 0;
  const openValidate = useCallback(() => {
    let handled = false;
    if (typeof window !== "undefined") {
      const evt = new CustomEvent("lilia:open-validate-rail", { cancelable: true });
      handled = !window.dispatchEvent(evt);
    }
    if (!handled) navigate(`/studio/${documentId}?tab=validate`);
  }, [navigate, documentId]);

  // Word export, one click from the top bar.
  //
  // It lives here rather than only inside the Export tab because a format
  // nobody can reach is a format nobody checks: the .docx silently dropped
  // titles, references and maths for as long as it existed, and the only way
  // to notice was to go looking for it. A button beside the layout switch
  // means any document is one click from being verified.
  const { fetchWithAuth } = useApiContext();
  const [exportingWord, setExportingWord] = useState(false);
  const firstExport = useFirstExportToast();

  const handleExportWord = useCallback(async () => {
    if (exportingWord) return;
    setExportingWord(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/export/docx`);
      if (!response.ok) throw new Error(`Export failed (${response.status})`);

      const blob = await response.blob();
      const href = URL.createObjectURL(blob);
      const a = window.document.createElement("a");
      a.href = href;
      a.download = `${(localTitle || "document").replace(/[^\w\s-]/g, "").trim() || "document"}.docx`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(href);
      firstExport("DOCX");
    } catch (err) {
      console.error("Word export failed:", err);
    } finally {
      setExportingWord(false);
    }
  }, [exportingWord, fetchWithAuth, documentId, localTitle, firstExport]);

  return (
    <>
      {/* The pre-Compact two-row bar lived here. Every call site now
          passes `compact`, so it was unreachable — and it carried a
          duplicate search and theme toggle, a Word button that also sits
          under the Export caret, a serif document title (a name is
          chrome, not document content), and two control outlines drawn on
          --lilia-border at 1.22:1, which is a divider weight and
          invisible as an outline. Deleted 14 Sep 2026 rather than
          restyled. */}

      {/* Compact: the same document controls, sized to sit in the shell bar.
          Search and the theme toggle are deliberately absent — the shell bar
          already has both, and carrying them twice is what made the second
          row look full. */}
      {compact && (
      <div className="flex items-center gap-1.5 shrink-0" data-testid="doc-actions">
        {/* The save indicator moved beside the document's name in the shell
            bar (LiliaTopBar's titleAccessory): its answer is about the
            document. Saved-moment handoff, 27 Sep 2026. */}

        {hasValidationIssues && (
          <button
            type="button"
            title={i18n.t("editor:ui.validationIssues")}
            data-testid="topbar-validation-issues"
            data-count={validationIssueCount}
            onClick={openValidate}
            className="hidden lg:inline-flex items-center gap-1.5 shrink-0 rounded-full cursor-pointer px-2 h-[20px] text-[11px]"
            // Opaque pair, not a colour-mix against transparent: the alpha
            // form measured 4.40:1. I wrote this one the day after the colour
            // handoff retired the pattern.
            style={{
              color: "var(--lilia-warning)",
              background: "var(--lilia-warning-bg)",
            }}
          >
            {i18n.t("editor:ui.issueCount", { count: validationIssueCount })}
          </button>
        )}

        {/* Export is the only FILLED control in the frame — that is what
            promotes it. It used to be a ghost button labelled with a single
            format, reading as a format chip rather than an action. */}
        <div className="inline-flex items-stretch shrink-0">
          <button
            type="button"
            data-testid={testIds.editor.docExport}
            onClick={() => setShowExportDialog(true)}
            title={i18n.t("editor:ui.exportDoc")}
            className="inline-flex items-center gap-1.5 h-[30px] pl-2.5 pr-2 rounded-l-[8px] text-[12px] font-medium transition-opacity hover:opacity-90"
            style={{ background: "var(--lilia-primary)", color: "var(--lilia-on-primary, #fff)" }}
          >
            <IconDownload size={14} aria-hidden />
            <span className="hidden md:inline">{i18n.t("editor:ui.export")}</span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid={testIds.editor.docExportMenu}
                title={i18n.t("editor:ui.otherFormats")}
                aria-label={i18n.t("editor:ui.otherExportFormats")}
                className="inline-flex items-center h-[30px] px-1.5 rounded-r-[8px] transition-opacity hover:opacity-90"
                style={{
                  background: "var(--lilia-primary)",
                  color: "var(--lilia-on-primary, #fff)",
                  borderLeft: "1px solid color-mix(in srgb, #000 18%, transparent)",
                }}
              >
                <IconChevronDown size={13} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[180px]">
              <DropdownMenuItem
                data-testid={testIds.editor.docExportWord}
                onClick={handleExportWord}
                disabled={exportingWord}
              >
                <IconDownload size={14} className="mr-2" />
                {exportingWord ? i18n.t("editor:ui.exporting") : i18n.t("editor:ui.word")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={testIds.editor.docExportSettings}
                onClick={() => setShowExportDialog(true)}
              >
                <IconPackage size={14} className="mr-2" /> {i18n.t("editor:ui.allFormats")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {documentObj && (
          <div className="shrink-0 hidden lg:flex items-center">
            <DocumentStamp
              document={documentObj}
              loading={documentLoading}
              readOnly={documentReadOnly}
            />
          </div>
        )}

        {/* A viewer's one way forward: their own copy (view-only handoff —
            secondary, since reading is still the main activity; also in ⋯). */}
        {documentReadOnly && documentId && (
          <button
            type="button"
            data-testid="topbar-make-copy"
            disabled={makingCopy}
            onClick={async () => {
              setMakingCopy(true);
              try {
                const id = await duplicateDoc(documentId);
                navigate(`/d/${id}`);
              } finally {
                setMakingCopy(false);
              }
            }}
            title={i18n.t("editor:ui.makeCopyTitle")}
            className={TOPBAR_CHIP}
          >
            <IconCopy size={14} /><span>{makingCopy ? i18n.t("editor:ui.copying") : i18n.t("editor:ui.makeCopy")}</span>
          </button>
        )}

        <button
          type="button"
          data-testid="topbar-share"
          onClick={() => setShowShareDialog(true)}
          title={t('share')}
          className={TOPBAR_CHIP}
        >
          <IconShare2 size={14} /><span className="hidden xl:inline">{t('share')}</span>
        </button>

        {/* ⋯ is back with four items (launch redesign 7a, Olivia 14 Oct): the user removed the old,
            longer menu on 27 Sep ("we will add some back"), and Document settings had no other way in
            on desktop: columns, paper, fonts, margins, the preamble and heading numbering. */}
        {documentId && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid={testIds.editor.docActionsOverflow}
                title={i18n.t("editor:ui.moreActions")}
                aria-label={i18n.t("editor:ui.moreActions")}
                className={TOPBAR_CHIP}
              >
                <IconDots size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              {!documentReadOnly && onOpenSettings && (
                <DropdownMenuItem data-testid={testIds.editor.moreSettings} onClick={onOpenSettings}>
                  <IconSettings size={14} className="mr-2" /> {i18n.t("editor:ui.documentSettingsEllipsis")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                data-testid={testIds.editor.moreDuplicate}
                onClick={async () => { const id = await duplicateDoc(documentId); navigate(`/d/${id}`); }}
              >
                <IconCopy size={14} className="mr-2" /> {i18n.t("editor:ui.duplicate")}
              </DropdownMenuItem>
              {!documentReadOnly && (
                <DropdownMenuItem data-testid={testIds.editor.moreSaveTemplate} onClick={() => setSaveTemplateOpen(true)}>
                  <IconTemplate size={14} className="mr-2" /> {i18n.t("editor:ui.saveAsTemplate")}
                </DropdownMenuItem>
              )}
              {documentObj?.role === "owner" && (
                <DropdownMenuItem
                  data-testid={testIds.editor.moreTrash}
                  onClick={async () => { await trashDoc(documentId, { reopen: true }); navigate("/documents"); }}
                >
                  <IconTrash size={14} className="mr-2" /> {i18n.t("editor:ui.moveToTrash")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      )}

      <SaveAsTemplateDialog
        open={saveTemplateOpen}
        onOpenChange={setSaveTemplateOpen}
        documentId={documentId ?? null}
        documentTitle={localTitle}
      />
      <ExportDialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        documentId={documentId}
        documentTitle={localTitle}
        look={documentObj?.look ?? null}
        lookLocked={documentObj?.lookLocked ?? null}
        lookThemes={documentObj?.lookThemes ?? null}
      />

      <ShareDrawer
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        documentId={documentId}
        documentTitle={localTitle}
        isPublic={isPublic}
        shareLink={shareLink}
        shareSlug={shareSlug}
        ownerId={ownerId}
      />
    </>
  );
}

interface ModeButtonProps {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

function ModeButton({ active, icon, label, onClick }: ModeButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 rounded transition-colors",
        active
          ? "bg-[var(--lilia-surface)] text-[var(--lilia-text)] shadow-sm"
          : "text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)]"
      )}
      style={{
        padding: "var(--editor-mode-padding-y, 6px) var(--editor-mode-padding-x, 8px)",
        fontSize: "var(--editor-mode-fontsize, 11px)",
        fontWeight: 500,
        borderRadius: "var(--editor-mode-radius, 6px)",
      }}
    >
      {icon}
      <span className="hidden lg:inline">{label}</span>
    </button>
  );
}
